import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Save, Home, Layers, Building2, Factory } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("bct_token")}` });
const emptyService = { title: "", subtitle: "", description: "", image: "", icon: "home", order: 0 };

const iconOptions = [
  { value: "home", label: "Konut", Icon: Home },
  { value: "layers", label: "Alt Yapı", Icon: Layers },
  { value: "building", label: "Ticari", Icon: Building2 },
  { value: "factory", label: "Endüstriyel", Icon: Factory },
];

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService);
  const [loading, setLoading] = useState(false);

  const load = () => axios.get(`${API}/api/services`).then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyService); setEditing("new"); };
  const openEdit = (s) => { setForm({ title: s.title, subtitle: s.subtitle, description: s.description, image: s.image, icon: s.icon, order: s.order }); setEditing(s); };
  const close = () => { setEditing(null); setForm(emptyService); };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editing === "new") await axios.post(`${API}/api/admin/services`, form, { headers: getHeaders(), withCredentials: true });
      else await axios.put(`${API}/api/admin/services/${editing.id}`, form, { headers: getHeaders(), withCredentials: true });
      close(); load();
    } catch { alert("Hata oluştu"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hizmeti silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/api/admin/services/${id}`, { headers: getHeaders(), withCredentials: true });
    load();
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Hizmetler</h1>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#FF5A00] hover:bg-[#e64f00] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors" data-testid="add-service-btn">
          <Plus size={16} /> Yeni Hizmet
        </button>
      </div>

      <div className="bg-[#161923] border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="services-table">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Hizmet</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Alt Başlık</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Sıra</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`service-row-${s.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <img src={s.image?.startsWith("/api") ? `${API}${s.image}` : s.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      <p className="text-white font-medium">{s.title}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-400 hidden md:table-cell">{s.subtitle}</td>
                  <td className="py-3 px-4 text-slate-400 hidden sm:table-cell">{s.order}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(s)} className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(s.id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {services.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-500">Henüz hizmet eklenmedi</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="service-modal">
          <div className="bg-[#1a1d27] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit" }}>{editing === "new" ? "Yeni Hizmet" : "Hizmet Düzenle"}</h2>
              <button onClick={close} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Başlık</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="admin-input" data-testid="service-form-title" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Alt Başlık</label>
                <input type="text" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="admin-input" data-testid="service-form-subtitle" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="admin-input resize-none" data-testid="service-form-description" />
              </div>
              <ImageUploader value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Hizmet Görseli" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">İkon</label>
                  <div className="flex gap-2">
                    {iconOptions.map(({ value, label, Icon }) => (
                      <button key={value} onClick={() => setForm({ ...form, icon: value })} className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors ${form.icon === value ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-white/10 text-slate-400 hover:text-white"}`}>
                        <Icon size={16} />
                        <span className="text-[10px]">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Sıra</label>
                  <input type="number" min={0} value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="admin-input" data-testid="service-form-order" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/5">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white border border-white/10 transition-colors">İptal</button>
              <button onClick={handleSave} disabled={loading || !form.title} className="flex items-center gap-2 px-4 py-2 bg-[#FF5A00] hover:bg-[#e64f00] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50" data-testid="service-form-save">
                <Save size={14} /> {loading ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
